export const HIERARCHY_TREE_UNDEFINED_PARENT_ID = 'ROOT';

export const HIERARCHY_TREE_CHILDREN_EMBEDDED = '_embedded';
export const HIERARCHY_TREE_CHILDREN = 'hierarchies';
export const HIERARCHY_TREE_PAGE = 'page';

export const HIERARCHY_TREE_NODE_ID = 'id';
export const HIERARCHY_TREE_NODE_NAME = 'name';
export const HIERARCHY_TREE_NODE_HAS_CHILDREN = 'hasChildren';

export interface HierarchyNodeFlat {
  id: number;
  parentId?: number;
}

export interface HierarchyTreeNodeBaseRaw extends HierarchyNodeFlat {
  children?: HierarchyTreeNodeBaseRaw[];
}

export interface HierarchyTreeNodeRaw extends HierarchyTreeNodeBaseRaw {
  // medication
  code?: string;
  label?: string;
  // organization
  organizationShortName?: string;
  organizationName?: string;
  organizationLongName?: string;
  levelType?: string;
}

export class HierarchyTreeNode {
  private readonly rawData: Partial<HierarchyTreeNodeRaw>;

  // eslint-disable-next-line no-use-before-define
  private readonly children: HierarchyTreeNode[];

  private readonly childIdx: number;

  static generateHierarchyNodeId(hierarchyId, id) {
    return (hierarchyId || '') + (id ? `_${id}` : '');
  }

  static initChildren(rawDataChildren?: HierarchyTreeNodeBaseRaw[]) {
    const children: HierarchyTreeNode[] = [];
    if (rawDataChildren && rawDataChildren.length > 0) {
      rawDataChildren.forEach((node, idx) => {
        children.push(new HierarchyTreeNode(node, idx));
      });
    }
    return children;
  }

  constructor(
    rawData: Partial<HierarchyTreeNodeRaw> = {},
    childIdx: number = 0
  ) {
    this.rawData = {...rawData};
    this.children = HierarchyTreeNode.initChildren(this.rawData.children);

    this.childIdx = childIdx;
  }

  getId() {
    return this.rawData.id;
  }

  getShortLabel() {
    return this.rawData.organizationShortName ?? this.rawData.code;
  }

  getLabel() {
    return this.rawData.organizationName ?? this.rawData.label;
  }

  getLongLabel() {
    return this.rawData.organizationLongName ?? this.rawData.label;
  }

  getLevelType() {
    return this.rawData.levelType;
  }

  getChildIdx() {
    return this.childIdx;
  }

  getChildren() {
    return this.children;
  }

  getChildCount() {
    return this.getChildren()?.length ?? 0;
  }

  getParentId() {
    return this.rawData.parentId;
  }

  hasChildren() {
    return this.getChildCount() > 0;
  }

  getRawData() {
    return this.rawData;
  }
}

export class HierarchyTreeChildren {
  private readonly rawData: HierarchyTreeNodeBaseRaw[];

  private readonly hierarchyId: number;

  private readonly nodes: HierarchyTreeNode[];

  static buildHierarchyTree(
    dataArray: HierarchyNodeFlat[],
    root: number | string
  ) {
    const t: Record<string, HierarchyTreeNodeBaseRaw> = {};
    dataArray.forEach((o) => {
      const parentId = o.parentId ?? HIERARCHY_TREE_UNDEFINED_PARENT_ID;
      // create (parent) map item for parentId if it does not exist
      t[parentId] = t[parentId] ?? {};
      // create children of that (parent) map item if it does not exist
      t[parentId].children = t[parentId].children ?? [];
      // create map item for id if it does not exist
      t[o.id] = t[o.id] ?? {};
      // create new child as object of id
      const newChild = Object.assign(t[o.id], o);
      // push created child into (parent) map item
      t[parentId].children?.push(newChild);
    });
    return t[root]?.children;
  }

  static initNodes(rawDataNodes: HierarchyTreeNodeBaseRaw[]) {
    const nodes: HierarchyTreeNode[] = [];
    if (rawDataNodes && rawDataNodes.length > 0) {
      rawDataNodes.forEach((node, idx) => {
        nodes.push(new HierarchyTreeNode(node, idx));
      });
    }
    return nodes;
  }

  constructor(
    rawData: HierarchyTreeNodeBaseRaw[] = [],
    hierarchyId: number = 0
  ) {
    this.rawData = [...rawData];
    this.hierarchyId = hierarchyId;
    this.nodes = [];

    if (this.rawData) {
      this.nodes = HierarchyTreeChildren.initNodes(this.rawData);
    }
  }

  getHierarchyId() {
    return this.hierarchyId;
  }

  getNodes() {
    return this.nodes;
  }
}

export default {HierarchyTreeChildren, HierarchyTreeNode};
